import { useState, useEffect } from "react";
import {
    Box,
    Button,
    VStack,
    HStack,
    Text,
    Badge,
    Input,
    Tag,
    TagLabel,
    TagCloseButton,
    Wrap,
    WrapItem,
    createStandaloneToast,
} from "@chakra-ui/react";
import Config from "config";

const toast = createStandaloneToast();
const HOST = Config.Host;

// Countries that are hit by the hardcoded geo penalties (see api/core.go)
const BLOCKED_HINT = [
    "IQ", "KW", "LY", "TN", "LB", "IN", "PK", "BR", "MX", "PH",
    "YE", "BD", "NP", "GH", "BH", "SA", "US", "GB",
];

export default function AllowTrafficFrom({ customerID, allowTrafficFrom = [] }) {
    const [countries, setCountries] = useState(allowTrafficFrom || []);
    const [input, setInput] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setCountries(allowTrafficFrom || []);
    }, [allowTrafficFrom]);

    const addCountry = () => {
        const codes = input
            .split(/[,\s]+/)
            .map((c) => c.trim().toUpperCase())
            .filter((c) => /^[A-Z]{2}$/.test(c));
        if (codes.length === 0) {
            toast({
                title: "Invalid input",
                description: "Use 2-letter ISO country codes (e.g. CN, RU, TW)",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setCountries((prev) => {
            const next = [...prev];
            codes.forEach((c) => {
                if (!next.includes(c)) next.push(c);
            });
            return next;
        });
        setInput("");
    };

    const removeCountry = (code) => {
        setCountries((prev) => prev.filter((c) => c !== code));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(
                `${HOST}/cc/customers/${customerID}/key/allowTrafficFrom?cors=true`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ value: countries }),
                }
            );
            if (response.ok) {
                toast({
                    title: "Saved",
                    description: "Allowed countries updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                throw new Error("Failed to save");
            }
        } catch (err) {
            toast({
                title: "Error",
                description: "Failed to update allowed countries",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <VStack align="stretch" spacing={4}>
                <Box>
                    <Text fontWeight="bold" fontSize="lg" mb={2}>
                        Allow traffic from
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={3}>
                        Countries on this list bypass the hardcoded geo
                        penalties (state limit, country rate limit, US/GB
                        penalty) for this customer/domain. Use 2-letter ISO
                        codes, e.g. CN, RU, TW.
                    </Text>
                    <HStack mb={3}>
                        <Input
                            placeholder="CN, RU, TW ..."
                            value={input}
                            maxW="300px"
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") addCountry();
                            }}
                        />
                        <Button size="md" onClick={addCountry}>
                            Add
                        </Button>
                    </HStack>
                    <Wrap>
                        {countries.length === 0 && (
                            <Text fontSize="sm" color="gray.400">
                                No countries whitelisted - hardcoded geo
                                penalties apply.
                            </Text>
                        )}
                        {countries.map((code) => (
                            <WrapItem key={code}>
                                <Tag
                                    size="lg"
                                    colorScheme={
                                        BLOCKED_HINT.includes(code)
                                            ? "orange"
                                            : "teal"
                                    }
                                    borderRadius="full"
                                >
                                    <TagLabel>{code}</TagLabel>
                                    <TagCloseButton
                                        onClick={() => removeCountry(code)}
                                    />
                                </Tag>
                            </WrapItem>
                        ))}
                    </Wrap>
                    {countries.some((c) => BLOCKED_HINT.includes(c)) && (
                        <HStack mt={3}>
                            <Badge colorScheme="orange">note</Badge>
                            <Text fontSize="xs" color="gray.500">
                                Orange entries override an active hardcoded
                                geo penalty.
                            </Text>
                        </HStack>
                    )}
                </Box>

                <Button
                    colorScheme="teal"
                    onClick={handleSave}
                    isLoading={saving}
                    loadingText="Saving..."
                    maxW="max-content"
                >
                    Save Allowed Countries
                </Button>
            </VStack>
        </Box>
    );
}
